import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'components/ui/use-toast';
import { useAuthContext } from 'modules/auth/context';
import { Profile } from 'modules/auth/api';
import { PurchasePublicTest } from 'modules/test/api';
import { useSetBalance } from 'modules/portal-plans/useSetBalance';
import { TestPaymentDialog, type TestPaymentTest } from './TestPaymentDialog';

interface TestStartPaymentProps {
  test: TestPaymentTest;
  /** The test is bought; the start page creates the attempt next. */
  onPurchased: () => void;
  onClose: () => void;
}

/**
 * The payment step of a paid test's start page: the price against the
 * learner's balance, a Payme or card top-up when it falls short, then the
 * purchase from balance. The start that follows spends the purchase, so the
 * learner is charged once and only after confirming here.
 */
export function TestStartPayment({ test, onPurchased, onClose }: TestStartPaymentProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { state } = useAuthContext();
  const setBalance = useSetBalance();
  const [purchasing, setPurchasing] = useState(false);

  // A top-up can land while the page is open, so what is due comes from a fresh balance.
  useEffect(() => {
    let cancelled = false;
    Profile()
      .then(({ data }) => {
        if (!cancelled) setBalance(data.user?.balanceSom);
      })
      .catch(() => {
        // Keep the last known balance. The purchase re-checks it.
      });
    return () => {
      cancelled = true;
    };
  }, [setBalance]);

  const confirm = async () => {
    if (purchasing) return;
    setPurchasing(true);
    try {
      const { data } = await PurchasePublicTest(test.id);
      setBalance(data?.balanceSom);
      onPurchased();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { code?: string; message?: string; balanceSom?: number } } }).response?.data;
      if (data?.code === 'INSUFFICIENT_BALANCE') {
        // The dialog stays open and recalculates what is due from the real balance.
        setBalance(data.balanceSom);
        toast.error(t('insufficient_balance'));
      } else {
        toast.error(t('test_purchase_failed'), { description: data?.message });
      }
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <TestPaymentDialog
      test={test}
      balanceSom={state.user?.balanceSom ?? 0}
      purchasing={purchasing}
      onConfirm={confirm}
      onBalanceChanged={setBalance}
      onClose={onClose}
    />
  );
}

export default TestStartPayment;
